import React from 'react'
import { Link } from 'react-router-dom'
import type { BusinessSection, Lang } from './types'

interface BusinessSplitBlockV2Props {
  section: BusinessSection
  currentLang: Lang
}

/**
 * BusinessSplitBlockV2
 * Reusable split block (image + text) used by BusinessPageV2 to render
 * each business pillar (門店 / 網購 / 批發). Image side alternates via
 * `section.mediaPosition`. Mobile: image always on top.
 */
const BusinessSplitBlockV2: React.FC<BusinessSplitBlockV2Props> = ({ section, currentLang }) => {
  const href = `/${currentLang}${section.hrefPath}`
  const isMediaRight = section.mediaPosition === 'right'

  return (
    <section className="bg-[#FAF9F1] py-12 md:py-16">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:items-center md:gap-16">
          {/* Image */}
          <div
            className={`md:col-span-7 ${isMediaRight ? 'md:order-2' : 'md:order-1'}`}
          >
            <Link to={href} className="group block overflow-hidden">
              <div className="relative aspect-[4/3] w-full overflow-hidden md:h-[520px] md:aspect-auto">
                <img
                  src={section.image}
                  alt={section.imageAlt[currentLang]}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
            </Link>
          </div>

          {/* Copy */}
          <div
            className={`md:col-span-5 ${isMediaRight ? 'md:order-1 md:pr-6' : 'md:order-2 md:pl-6'}`}
          >
            <p
              className="mb-5 text-[12px] tracking-[0.22em] text-black/65"
              style={{
                fontFamily: "'EB Garamond', serif",
                fontStyle: 'italic',
                fontWeight: 400,
              }}
            >
              {section.eyebrow}
            </p>

            <h2
              className="mb-3 text-black"
              style={{
                fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
                fontWeight: 400,
                fontSize: 'clamp(1.5rem, 2.6vw, 2rem)',
                letterSpacing: '0.04em',
                lineHeight: 1.3,
              }}
            >
              {section.title[currentLang]}
            </h2>

            <p
              className="mb-8 text-[14px] tracking-[0.05em] text-black/70"
              style={{
                fontFamily: "'Noto Serif TC', sans-serif",
                fontWeight: 300,
              }}
            >
              {section.subtitle[currentLang]}
            </p>

            <p
              className="mb-10 text-[15px] leading-loose text-black/75"
              style={{
                fontFamily: "'Noto Serif TC', 'Noto Sans TC', sans-serif",
                fontWeight: 300,
                letterSpacing: '0.03em',
              }}
            >
              {section.description[currentLang]}
            </p>

            <Link
              to={href}
              onClick={() => window.scrollTo(0, 0)}
              className="group inline-flex w-fit items-center text-sm tracking-[0.2em] text-black"
              style={{ fontFamily: "'EB Garamond', serif" }}
            >
              <span className="border-b border-black pb-1 transition-all duration-300 group-hover:tracking-[0.28em]">
                {section.cta[currentLang]}
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

export default BusinessSplitBlockV2
